import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import { fetchInstagramAnonymouslySearchData } from '@/lib/service/anonymously';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { message } from 'antd';
import { debounce } from 'lodash-es';
import { addInsStory } from '@/lib/service/dashboard';
import { AccountLimitAlert, LimitAlert } from '../LandingPage/FeeLimitAlert';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
import LocalImage from '../Common/LocalImage';
import { EventName } from '@/shared/event-types';
import { ga_logEvent } from '@/shared/ga';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import useGuestViewerSearchUsageState from '@/lib/hooks/common/useGuestViewerSearchUsageState';
import useUserViewerSearchUsageState from '@/lib/hooks/common/useUserViewerSearchUsageState';
import InstagramStoresFree from '../view-instagram-anonymously/InstagramStoresFree';
import UserDashboard from '../Dashboard/UserDashboard';
import { InsAccountModel } from '@/types/home';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';

const ChooseContent = dynamic(() => import('../LandingPage/ChooseContent'), {
  ssr: true,
});
const FAQContent = dynamic(() => import('./FAQContent'), { ssr: true });
const ChooseViewerForInstagramContent = dynamic(() => import('./ChooseViewerForInstagram'), {
  ssr: true,
});
const TopSearchContent = dynamic(() => import('../LandingPage/TopSearch'), { ssr: true });
// CLS 优化：骨架屏组件
import SearchResultSkeleton from './SearchResultSkeleton';
// CLS 优化：SearchResultContent 使用同步导入，确保骨架屏和内容在同一帧切换
import SearchResultContent from '../LandingPage/SearchResultContent';
const UseViewerForInstagramContent = dynamic(() => import('./UseViewerForInstagram'), {
  ssr: true,
});
const LandingPagePricingContent = dynamic(() => import('../LandingPage/PricingContent'), {
  ssr: true,
});

const WebViewerForInstagramContent = () => {
  const router = useRouter();
  const { media_name } = router.query;

  const [currentModel, setCurrentModel] = useState<InstagramAnonymouslyModel>(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const chooseContentData = {
    imgPath: '/images/viewer/choose-content-left.jpg',
    parts: [
      {
        imgPath: '/images/viewer/choose-icon-1.webp',
        title: t('View Full Profiles'),
        desc: t(
          'See more than just the latest posts — dive into public Instagram profiles to get the full picture, including bios, follower counts, and more from any public account.'
        ),
      },
      {
        imgPath: '/images/viewer/choose-icon-2.webp',
        title: t('Watch Stories & Highlights'),
        desc: t(
          'Stay updated with temporary content. View active and archived stories, as well as profile highlights, all from public accounts.'
        ),
      },
      {
        imgPath: '/images/viewer/choose-icon-3.webp',
        title: t('Smart Content Search'),
        desc: t(
          "Looking for something specific? Use our search feature to explore any public Instagram account's content quickly, without needing to scroll endlessly."
        ),
      },
    ],
    title: t('Instagram Post Viewer with Stories, Profiles, and Highlights'),
    desc: '',
  };

  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const { userUsage, consumeUserFreeTimes, fetchTimesRequest, syncRemainingTimesData } =
    useUserViewerSearchUsageState();
  const { guestUsage, consumeFreeTimes } = useGuestViewerSearchUsageState({
    search_limit: 1,
    search_count: 0,
  });
  const [showLimitPage, setShowLimitPage] = useState<boolean>(false);
  const [showAccountLimit, setShowAccountLimit] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [loginState, setLoginState] = useState(isLogin);
  const [searchStr, setSearchStr, writeToStorage] = useMediaNameSync(media_name as string, {
    canRead: false,
  });
  const [inputVisible, setInputVisible] = useState(true);
  const [insListLoading, setInsListLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const [errorCode, setErrorCode] = useState<string | number>(0);
  // CLS 优化：标记是否因错误需要保持骨架屏显示
  const [keepSkeletonOnError, setKeepSkeletonOnError] = useState(false);
  const isLockClick = useRef(false);
  const { isPremium } = useStoryPremium();
  // CLS 优化：控制内容显示时机，确保骨架屏和内容在同一帧切换
  const [showContent, setShowContent] = useState(false);

  const isGuestLocked = useMemo(() => {
    return guestUsage?.search_limit === 0 && guestUsage?.search_count >= 1;
  }, [guestUsage]);

  const isLocked = useMemo(() => {
    if (isLogin) {
      return (
        userUsage?.search_limit === 0 &&
        !userUsage?.first_free_auto_search &&
        !userUsage?.first_free_search &&
        !userUsage.un_limit
      );
    } else {
      return isGuestLocked;
    }
  }, [userUsage, isLogin, isGuestLocked]);

  const getNoStoriesDetectedContent = (img?: string, name?: string) => {
    return (
      <div style={{ paddingTop: '15px', marginBottom: '50px' }}>
        <p style={{ textAlign: 'center' }}>
          {t(
            'This account hasn’t posted any Stories in the last 24 hours. Add it to your tracking list for long-term monitoring'
          )}
        </p>
        <div
          style={{
            background: '#F1EFFF',
            borderRadius: '24px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          <LocalImage
            src={img}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              overflow: 'hidden',
            }}
          />
          <span style={{ marginLeft: '10px', fontWeight: '500' }}>{name ?? ''}</span>
        </div>
      </div>
    );
  };

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value) => {
        setLoginState(value);
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  }, [debouncedChangeHandler]);

  useEffect(() => {
    if (loginState) {
      fetchTimesRequest(() => {
        if (!!currentModel && isGuestLocked) {
          // 登录时，同步剩余次数
          syncRemainingTimesData(loginState);
        }
      }, loginState);
    }
  }, [loginState, isGuestLocked]);

  useEffect(() => {
    setLoginState(isLogin);

    if (!isLogin) {
      setCurrentModel(null);
    } else {
      if (searchStr && isLockClick.current) {
        onSearchClick(searchStr);
        isLockClick.current = false;
      }
    }
  }, [isLogin]);

  const onValueChanged = (e?: string) => {
    setSearchStr(e ?? '');
  };

  const onSearch = (e?: string) => {
    if (e !== searchStr && !currentModel) {
      setCurrentModel(null);
    }
    setSearchStr(e ?? '');

    // 增加未登录免费试用后，搜索时不再检查登录状态
    onSearchClick(e);
  };

  const onSearchClick = (e?: string) => {
    if (e?.length > 0) {
      // 手动写入搜索内容到 SessionStorage
      if (e.includes('/reel/') || e.includes('/reels/')) {
        // CLS 优化：错误时保持骨架屏显示，直到用户关闭弹窗
        setLoading(true);
        setKeepSkeletonOnError(true);
        setAlertInfo({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
          type: 'Instagram-Account-Not-Found',
        });
        return;
      }
      writeToStorage(e);

      if (loading) {
        return;
      }
      setLoading(true);
      // CLS 优化：搜索开始时重置 showContent，显示骨架屏预占位
      // 注意：点击后 500ms 内的布局变化不计入 CLS（hadRecentInput）
      setShowContent(false);
      fetchInstagramAnonymouslySearchData(e, null, isLogin ? 0 : isGuestLocked ? 1 : 0, {
        isPremium,
        bizFuncName: 'stories_viewer',
      })
        .then((res) => {
          if (res?.code === 0) {
            if (!isLogin) {
              // 未登录时，消耗免费次数并记录时间
              consumeFreeTimes();
            } else {
              // 登录时，消耗免费次数
              consumeUserFreeTimes();
            }

            if (res.data.private_mark) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.private_mark ? 1 : 0,
                profile_pic_url: res.data.avatar,
                media_name: res.data.media_name,
              };
              // CLS 优化：私密账户也需要批量更新状态
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
              setAlertInfo({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });

              return;
            }

            // CLS 优化核心：doShowContent 将所有状态更新集中在一起
            // 根据 React 18 自动批处理机制，同一同步函数内的多个 setState 只触发一次渲染
            // 这样骨架屏消失和内容出现在同一帧内完成，不会产生 CLS
            const doShowContent = () => {
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
            };

            // 先滚动到目标位置，滚动完成后再显示内容
            // 这样即使骨架屏和内容高度有差异，CLS 也发生在视口边缘或外部
            if (searchResultRef.current) {
              const targetTop = searchResultRef.current.offsetTop - 120;
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
              // 等待滚动完成后再显示内容
              let scrollTimer: NodeJS.Timeout;
              const handleScrollEnd = () => {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                  window.removeEventListener('scroll', handleScrollEnd);
                  doShowContent();
                }, 100);
              };
              window.addEventListener('scroll', handleScrollEnd);
              handleScrollEnd(); // 立即检查一次，以防不需要滚动
            } else {
              doShowContent();
            }
          } else if (res?.code === 22001 || res?.code === 22006) {
            fetchTimesRequest(undefined, isLogin);
            setShowLimitPage(true);
            // 错误时重置 loading，保留之前的 showContent 状态
            setLoading(false);
          } else if (res?.code === 22004 || res?.code === 22004) {
            fetchTimesRequest(undefined, isLogin);
            setErrorCode(res?.code);
            setSearchStr('');
            setCurrentModel(null);
            setLoading(false);
          } else if (res?.code === 22002) {
            // CLS 优化：错误时保持骨架屏显示，直到用户关闭弹窗
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
              type: 'Instagram-Account-Not-Found',
            });
            // 注意：不调用 setLoading(false)，保持骨架屏显示
          } else {
            message.error(res?.message);
            setLoading(false);
          }
        })
        .catch((error) => {
          setLoading(false);
        });
    } else {
      message.error(t('Please enter the insert link or username'));
    }
  };

  const onAddAccountToTrackClick = (media_name?: string) => {
    if (insListLoading) {
      return;
    }
    setInsListLoading(true);

    addInsStory(media_name)
      ?.then((res) => {
        if (res?.code === 0) {
          message.success(t('Added successfully'));
          router.push('/dashboard?dashboard_type=toolkit');
        } else {
          if (res?.code !== 22008) {
            message.error(res?.message);
          }

          if (res?.code === 22007) {
            setShowLimitPage(true);
          } else if (res?.code === 22008) {
            setShowAccountLimit(true);
          }
        }
      })
      .finally(() => {
        setInsListLoading(false);
      });
  };

  const gotoPlanClick = () => {
    if (priceRef.current) {
      const targetTop = priceRef.current.offsetTop - 60;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  // 未登录无免费次数，点击登录
  const handleLogin = () => {
    onOpenLoginModal(true);
    onChangedLoginView(false);
  };

  const handleUnlock = () => {
    isLockClick.current = true;
    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
    } else {
      gotoPlanClick();
    }
  };

  return (
    <>
      <CommonBackContent
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          background: 'transparent',
          minHeight: '100vh',
        }}
      >
        <TopSearchContent
          topInfo={{
            title: t('Post Viewer for Instagram'),
            desc: t(
              `An Instagram Post Viewer lets you view posts, stories, and profiles from public accounts with ease. With Dolphin Radar, you can even download the content you care about in just a few clicks. Whether you're looking for a Post Viewer, Story Viewer, or Profile Viewer, our all-in-one tool is ready for you.`
            ),
          }}
          loading={loading}
          leftModel={userUsage}
          guestUsage={guestUsage}
          searchVal={searchStr}
          btnPlaceholder={t('View')}
          onSearchClick={(e) => {
            onSearch(e);
            // ga_logEvent(EventName.GA_search_now_clicks)
          }}
          onValueChanged={onValueChanged}
          onInputVisibleChanged={(visible) => setInputVisible(visible)}
          onUnlimitedClick={() => {
            const dom = document.getElementById('free-tools-title');
            if (dom) {
              dom.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          onLoginClick={handleLogin}
          errorCode={errorCode}
          resetErrorCode={() => setErrorCode(0)}
        />

        {/* CLS 优化：loading 时显示骨架屏预占位，数据返回后同帧切换到真实内容 */}
        <div ref={searchResultRef}>
          {(loading || keepSkeletonOnError) && !showContent ? (
            <SearchResultSkeleton />
          ) : showContent && currentModel ? (
            <SearchResultContent
              locked={isLocked}
              loading={insListLoading}
              loadMoreNotCheckLogin={true}
              tabFunc="post"
              post_page_token={currentModel?.post_page_token}
              data={currentModel}
              onAddAccountToTrackClick={onAddAccountToTrackClick}
              onPlanClick={() => setShowLimitPage(true)}
              onUnlock={handleUnlock}
            />
          ) : null}
        </div>

        <ChooseContent data={chooseContentData} />
        <ChooseViewerForInstagramContent />
        <UseViewerForInstagramContent />
        <div ref={priceRef} id="price">
          <LandingPagePricingContent func="story" />
        </div>
        <InstagramStoresFree
          fetchTimesRequest={() => {
            fetchTimesRequest(undefined, isLogin);
            const dom = document.getElementById('search-now-button');
            if (dom) {
              dom.click();
              dom.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          tabFunc="post"
        />
      </CommonBackContent>
      <FAQContent />

      <LimitAlert
        open={showLimitPage}
        closeAction={(isOk) => {
          setShowLimitPage(false);

          if (isOk) {
            // ga_logEvent(EventName.GA_search_now_limit_notify_clicked)
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <AccountLimitAlert
        open={showAccountLimit}
        closeAction={(isOk) => {
          setShowAccountLimit(false);

          if (isOk) {
            ga_logEvent(EventName.GA_account_limit_reached_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (isOk && alertInfo?.href) {
            if (alertInfo.type == 'No-Stories-Detected') {
              setAlertInfo(null);
              router.push(
                `/web-viewer-for-instagram?media_name=${alertInfo?.media_name}&avatar=${alertInfo?.avatar}&media_url=${alertInfo?.media_url}#1`
              );
              return;
            }
            router.push(alertInfo?.href);
          }

          // CLS 优化：用户关闭弹窗后，清除错误状态并隐藏骨架屏
          if (keepSkeletonOnError) {
            setKeepSkeletonOnError(false);
            setLoading(false);
          }

          if (isOk) {
            if ('Out-of-Views' === alertInfo?.type) {
            } else {
              setAlertInfo(null);
            }
          } else {
            setAlertInfo(null);
          }
        }}
      />
    </>
  );
};

export default WebViewerForInstagramContent;
